/**
 * Le diagnostic d'une heure : ce que coûte l'administratif d'un prospect.
 *
 * Mené en rendez-vous depuis la console staff, jamais par le prospect seul.
 * Le calculateur public de la landing (`LANDING DKER/calculateur/`) fait le
 * même calcul en JavaScript sans build, et **les deux doivent bouger
 * ensemble** : mêmes postes, mêmes parts reprises, mêmes durées.
 *
 * Les parts reprises par DKER sont des estimations, affichées comme telles et
 * modifiables en rendez-vous : aucune n'est mesurée chez un client.
 */

/** Une année travaillée. */
export const DIAGNOSTIC_SEMAINES = 47;
export const DIAGNOSTIC_MOIS = 11;
export const DIAGNOSTIC_HEURES_JOUR = 7;

export type QuiFaitAdmin = "patron" | "moitie" | "bureau";

export interface DiagnosticInput {
  equipe: number;
  /** Chiffre d'affaires annuel HT. */
  ca: number;
  qui: QuiFaitAdmin;
  /** Ce qu'une heure du patron rapporte facturée sur un chantier, HT. */
  tauxPatron: number;
  /** Coût horaire chargé de la personne au bureau. */
  tauxBureau: number;

  devisParMois: number;
  minutesParDevis: number;
  montantMoyenDevis: number;
  /** Part des devis sans réponse jamais relancés : 0, 0,5 ou 1. */
  partDevisNonRelances: number;

  facturesParMois: number;
  minutesParFacture: number;
  /** Jours entre la fin d'une étape et l'envoi de sa facture. */
  delaiFacturationJours: number;
  impayesEchus: number;
  minutesRelanceParSemaine: number;

  minutesPointageParSemaine: number;
  minutesPlanningParSemaine: number;

  commandesParSemaine: number;
  minutesParCommande: number;
  facturesFournisseursParMois: number;
  minutesParFactureFournisseur: number;

  /** Coût de l'argent immobilisé, en % par an. */
  tauxTresorerie: number;
  /** Part des devis non relancés qu'une relance ferait signer, en %. */
  rattrapageRelance: number;
  /** Part reprise par DKER, poste par poste, en %. */
  reprise: Record<PosteId, number>;
}

export type PosteId =
  | "devis"
  | "factures"
  | "relances"
  | "pointage"
  | "planning"
  | "commandes"
  | "fournisseurs";

export interface Poste {
  id: PosteId;
  label: string;
  /** Ce que DKER fait à la place du patron, dit en une ligne. */
  pourquoi: string;
  repriseParDefaut: number;
  heuresParAn: (v: DiagnosticInput) => number;
}

const S = DIAGNOSTIC_SEMAINES;
const M = DIAGNOSTIC_MOIS;

export const DIAGNOSTIC_POSTES: readonly Poste[] = [
  { id: "devis", label: "Devis", pourquoi: "dictée du devis, bibliothèque de prix", repriseParDefaut: 50,
    heuresParAn: (v) => (v.devisParMois * v.minutesParDevis) / 60 * M },
  { id: "factures", label: "Factures et situations", pourquoi: "facture tirée du devis ou de l’avancement", repriseParDefaut: 70,
    heuresParAn: (v) => (v.facturesParMois * v.minutesParFacture) / 60 * M },
  { id: "relances", label: "Relances d’impayés", pourquoi: "préparées le soir, le patron valide", repriseParDefaut: 80,
    heuresParAn: (v) => v.minutesRelanceParSemaine / 60 * S },
  { id: "pointage", label: "Heures des équipes", pourquoi: "les équipes pointent sur leur téléphone", repriseParDefaut: 75,
    heuresParAn: (v) => v.minutesPointageParSemaine / 60 * S },
  { id: "planning", label: "Planning", pourquoi: "grille partagée, conflits signalés", repriseParDefaut: 40,
    heuresParAn: (v) => v.minutesPlanningParSemaine / 60 * S },
  { id: "commandes", label: "Commandes", pourquoi: "panier réparti par fournisseur", repriseParDefaut: 50,
    heuresParAn: (v) => (v.commandesParSemaine * v.minutesParCommande) / 60 * S },
  { id: "fournisseurs", label: "Factures fournisseurs", pourquoi: "réception électronique, ticket lu en photo", repriseParDefaut: 70,
    heuresParAn: (v) => (v.facturesFournisseursParMois * v.minutesParFactureFournisseur) / 60 * M },
];

/** Les valeurs d'exemple : une entreprise de quatre personnes. */
export const DIAGNOSTIC_EXEMPLE: DiagnosticInput = {
  equipe: 4,
  ca: 600_000,
  qui: "patron",
  tauxPatron: 50,
  tauxBureau: 25,
  devisParMois: 8,
  minutesParDevis: 60,
  montantMoyenDevis: 4_000,
  partDevisNonRelances: 0.5,
  facturesParMois: 8,
  minutesParFacture: 20,
  delaiFacturationJours: 10,
  impayesEchus: 8_000,
  minutesRelanceParSemaine: 30,
  minutesPointageParSemaine: 60,
  minutesPlanningParSemaine: 90,
  commandesParSemaine: 3,
  minutesParCommande: 15,
  facturesFournisseursParMois: 25,
  minutesParFactureFournisseur: 5,
  tauxTresorerie: 6,
  rattrapageRelance: 10,
  reprise: Object.fromEntries(DIAGNOSTIC_POSTES.map((p) => [p.id, p.repriseParDefaut])) as Record<PosteId, number>,
};

export interface DiagnosticResult {
  tauxHoraire: number;
  postes: { id: PosteId; label: string; heuresParAn: number; heuresReprisesParAn: number }[];
  heuresParAn: number;
  heuresReprisesParAn: number;
  coutTemps: number;
  /** Le chiffre d'affaires que le délai de facturation retient en permanence. */
  retenuParDelai: number;
  argentQuiDort: number;
  coutArgentQuiDort: number;
  /** Temps et coût de l'argent immobilisé : le chiffre annoncé. */
  coutTotal: number;
  gainReprise: number;
  /** Chiffre d'affaires qu'une relance systématique pourrait rattraper. */
  devisRattrapables: number;
}

const positif = (n: number) => (Number.isFinite(n) && n > 0 ? n : 0);

export function tauxHoraireAdmin(v: Pick<DiagnosticInput, "qui" | "tauxPatron" | "tauxBureau">): number {
  const p = positif(v.tauxPatron);
  const b = positif(v.tauxBureau);
  if (v.qui === "moitie") return (p + b) / 2;
  if (v.qui === "bureau") return 0.2 * p + 0.8 * b;
  return p;
}

export function computeDiagnostic(raw: DiagnosticInput): DiagnosticResult {
  // Une saisie vide ou négative vaut zéro : on calcule pendant la frappe.
  const v = { ...raw } as DiagnosticInput;
  for (const k of Object.keys(v) as (keyof DiagnosticInput)[]) {
    if (typeof v[k] === "number") (v as unknown as Record<string, number>)[k] = positif(v[k] as number);
  }

  const taux = tauxHoraireAdmin(v);
  const postes = DIAGNOSTIC_POSTES.map((p) => {
    const heuresParAn = p.heuresParAn(v);
    const part = Math.min(positif(v.reprise[p.id] ?? 0), 100) / 100;
    return { id: p.id, label: p.label, heuresParAn, heuresReprisesParAn: heuresParAn * part };
  });
  const heuresParAn = postes.reduce((s, x) => s + x.heuresParAn, 0);
  const heuresReprisesParAn = postes.reduce((s, x) => s + x.heuresReprisesParAn, 0);

  const coutTemps = heuresParAn * taux;
  const retenuParDelai = (v.ca / 365) * v.delaiFacturationJours;
  const argentQuiDort = v.impayesEchus + retenuParDelai;
  const coutArgentQuiDort = (argentQuiDort * v.tauxTresorerie) / 100;

  const devisRattrapables =
    v.devisParMois * M * Math.min(v.partDevisNonRelances, 1) * (v.rattrapageRelance / 100) * v.montantMoyenDevis;

  return {
    tauxHoraire: taux,
    postes,
    heuresParAn,
    heuresReprisesParAn,
    coutTemps,
    retenuParDelai,
    argentQuiDort,
    coutArgentQuiDort,
    coutTotal: coutTemps + coutArgentQuiDort,
    gainReprise: heuresReprisesParAn * taux,
    devisRattrapables,
  };
}
